import { StyleProp, StyleSheet, View, ViewStyle } from 'react-native';

import { ChallengeHomeCard } from '@fhs/loyalty/src/components/challenge-home-card';
import { ChallengeHomeCardSkeleton } from '@fhs/loyalty/src/components/challenge-home-card-skeleton';
import { DoublePointsMondayBanner } from '@fhs/loyalty/src/components/double-points-monday-banner';
import { OrderDisplaySkeleton, RapidRescueCard } from '@fhs/ui';
import { OrderDisplay } from '@fhs/ui/src/components/order-display';
import { OrderDisplayType } from '@fhs/ui/src/components/order-display/types';
import { useDebounce } from '@fhs-legacy/frontend/src/hooks/use-debounce';
import { useServiceModeContext } from '@fhs-legacy/frontend/src/state/service-mode';

import { ScreenHomeProps } from '../types';

type HomeCardProps = Pick<
  ScreenHomeProps,
  | 'displayedOrder'
  | 'displayedOrderCardLoading'
  | 'redirectPath'
  | 'onReorder'
  | 'reorderLoading'
  | 'logReorderCardPress'
  | 'isLoadingChallenges'
  | 'loyaltyUserReady'
  | 'isDesktop'
  | 'challengeHomeCardProps'
  | 'doublePointsMondayBanner'
> & {
  noEmptyState?: boolean;
  style?: StyleProp<ViewStyle>;
};

export function HomeCard({
  style,
  displayedOrderCardLoading,
  displayedOrder,
  isLoadingChallenges,
  loyaltyUserReady,
  redirectPath,
  reorderLoading,
  onReorder,
  logReorderCardPress,
  isDesktop,
  challengeHomeCardProps,
  doublePointsMondayBanner,
  noEmptyState = false,
}: HomeCardProps) {
  const containerStyle = [styles.container, style];

  const isLoading = isLoadingChallenges || !loyaltyUserReady || displayedOrderCardLoading;
  const debouncedIsLoading = useDebounce(isLoading, 300);
  const { isDelivery } = useServiceModeContext();

  const shouldDisplayOrder =
    displayedOrder && displayedOrder.serviceModeTitle !== 'Delivery Order' && !isDelivery;
  const isReorder = displayedOrder && displayedOrder.type === OrderDisplayType.REORDER;

  const dpmBannerImageUrl = doublePointsMondayBanner?.isDoublePointsMondayOn
    ? doublePointsMondayBanner.bannerImageUrl
    : null;

  const dpmBannerElement = dpmBannerImageUrl ? (
    <DoublePointsMondayBanner
      bannerImageUrl={dpmBannerImageUrl}
      onPress={doublePointsMondayBanner?.onBannerPress}
    />
  ) : null;

  const orderDisplay = shouldDisplayOrder ? (
    <OrderDisplay
      testID="order-display"
      type={displayedOrder.type}
      href={redirectPath}
      storeName={displayedOrder.storeName}
      items={displayedOrder.items}
      serviceModeTitle={displayedOrder.serviceModeTitle}
      reorderCallback={onReorder}
      reorderLoading={reorderLoading}
      logReorderCardPress={logReorderCardPress}
    />
  ) : null;

  // Priority: order in progress → challenge → double points monday → reorder
  if (isDesktop && isLoading) {
    return null;
  }

  if (isLoadingChallenges || !loyaltyUserReady) {
    return (
      <View testID="challenge-home-skeleton" style={containerStyle}>
        <ChallengeHomeCardSkeleton />
      </View>
    );
  }

  if (debouncedIsLoading) {
    return (
      <View testID="order-display-skeleton" style={containerStyle}>
        <OrderDisplaySkeleton />
      </View>
    );
  }
  // 1. Order in progress (non-reorder)
  if (shouldDisplayOrder && !isReorder) {
    return <View style={containerStyle}>{orderDisplay}</View>;
  }

  // 2. Challenge
  if (challengeHomeCardProps) {
    return (
      <View testID="challenge-home-card" style={containerStyle}>
        <ChallengeHomeCard {...challengeHomeCardProps} />
      </View>
    );
  }

  // 3. Double Points Monday banner
  if (dpmBannerImageUrl) {
    return <View style={containerStyle}>{dpmBannerElement}</View>;
  }

  // 4. Reorder
  if (shouldDisplayOrder) {
    return <View style={containerStyle}>{orderDisplay}</View>;
  }

  if (noEmptyState) {
    return null;
  }

  return (
    <View testID="rapid-rescue-card" style={[styles.rapidRescueContainer, style]}>
      <RapidRescueCard />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    paddingHorizontal: 16,
  },
  rapidRescueContainer: {
    width: '100%',
    paddingHorizontal: 16,
  },
});
